"use client"

import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from "nuqs"
import { useEffect, useState } from "react"
import { Input } from "@/components/ui/input"
import { Slider } from "@/components/ui/slider"
import type { CategoryAvailableFiltersResult } from "@/services/woo-commerce-service"
import { RatingFilter } from "~/components/e-commerce/category/sidebar/rating-filter"
import type { SortBy, SortOrder, ViewMode } from "../query-params/contract"

const PRICE_MIN = 0
const PRICE_MAX = 5000
const PRICE_RANGE_LENGTH = 2

const categoryQueryParsers = {
  q: parseAsString.withDefault(""),
  orderby: parseAsStringLiteral<SortBy>([
    "menu_order",
    "date",
    "popularity",
    "rating",
    "title",
  ]),
  order: parseAsStringLiteral<SortOrder>(["asc", "desc"]),
  view: parseAsStringLiteral<ViewMode>(["grid", "list"]).withDefault("grid"),
  per_page: parseAsInteger,
  store_ids: parseAsArrayOf(parseAsString, ",").withDefault([]),
  brand: parseAsString,
  min_rating: parseAsInteger,
  discount: parseAsStringLiteral(["1", "true"]),
  min_price: parseAsInteger,
  max_price: parseAsInteger,
}

interface CategorySidebarFiltersProps {
  availableFilters: CategoryAvailableFiltersResult
}

export const CategorySidebarFilters = ({
  availableFilters: { stores, priceRange: price, brands, ratings },
}: CategorySidebarFiltersProps) => {
  const [query, setQuery] = useQueryStates(categoryQueryParsers, {
    history: "replace",
    shallow: false,
  })

  const [priceRange, setPriceRange] = useState<[number, number]>([
    price.min,
    price.max,
  ])

  const priceRangeMin = query.min_price ?? price.min ?? PRICE_MIN
  const priceRangeMax = query.max_price ?? price.max ?? PRICE_MAX

  useEffect(() => {
    setPriceRange([priceRangeMin, priceRangeMax])
  }, [priceRangeMin, priceRangeMax])

  const updateFilter = (patch: Partial<typeof query>) => {
    setQuery(patch)
  }

  const toggleStore = (storeId: string, checked: boolean) => {
    const storeIds = checked
      ? [...new Set([...query.store_ids, storeId])]
      : query.store_ids.filter((id) => id !== storeId)

    updateFilter({ store_ids: storeIds })
  }

  const commitPriceRange = (range: [number, number]) => {
    updateFilter({
      min_price: Math.round(range[0]),
      max_price: Math.round(range[1]),
    })
  }

  const handlePriceRangeChange = (value: [number, number]) => {
    if (value.length !== PRICE_RANGE_LENGTH) {
      return
    }

    setPriceRange(value)
  }

  const handlePriceRangeCommit = (value: [number, number]) => {
    if (value.length !== PRICE_RANGE_LENGTH) {
      return
    }

    setPriceRange(value)
    commitPriceRange(value)
  }

  return (
    <aside className='h-fit space-y-5 rounded-md border border-gray-200 bg-gray-50 p-4 lg:sticky lg:top-24 lg:block'>
      <div className='space-y-2'>
        <label
          className='font-semibold text-gray-800 text-sm'
          htmlFor='category-q'
        >
          Busca
        </label>
        <Input
          id='category-q'
          onChange={(event) => updateFilter({ q: event.target.value })}
          placeholder='Buscar produtos'
          type='search'
          value={query.q}
        />
      </div>

      <div className='space-y-2'>
        <p className='font-semibold text-gray-800 text-sm'>Faixa de Preço</p>
        <div className='space-y-2'>
          <Slider
            aria-label='Faixa de preço'
            max={price.max}
            min={price.min}
            onValueChange={handlePriceRangeChange}
            onValueCommit={handlePriceRangeCommit}
            step={10}
            value={priceRange}
          />
          <div className='flex items-center justify-between text-gray-500 text-sm'>
            <span>R$ {priceRange[0]}</span>
            <span>R$ {priceRange[1]}</span>
          </div>
        </div>
      </div>

      <div className='space-y-2'>
        <p className='font-semibold text-gray-800 text-sm'>Lojas</p>
        <div className='space-y-1'>
          {stores.length === 0 && (
            <p className='text-gray-500 text-xs'>Nenhuma loja disponível.</p>
          )}
          {stores.map((store) => {
            const checkboxId = `store-${store.id}`
            const checked = query.store_ids.includes(store.id)

            return (
              <label
                className='flex cursor-pointer items-center gap-2 text-gray-700 text-sm'
                htmlFor={checkboxId}
                key={store.id}
              >
                <input
                  checked={checked}
                  className='h-4 w-4 accent-primary'
                  id={checkboxId}
                  onChange={(event) =>
                    toggleStore(store.id, event.target.checked)
                  }
                  type='checkbox'
                />
                <span>{store.label}</span>
              </label>
            )
          })}
        </div>
      </div>

      <div className='space-y-2'>
        <label
          className='font-semibold text-gray-800 text-sm'
          htmlFor='category-brand'
        >
          Marca
        </label>
        <Input
          id='category-brand'
          onChange={(event) =>
            updateFilter({ brand: event.target.value || null })
          }
          placeholder='Ex.: Prime'
          value={query.brand ?? ""}
        />
        {brands && brands.length > 0 && (
          <p className='text-gray-500 text-xs'>
            Marcas disponíveis:{" "}
            {brands
              .slice(0, 6)
              .map((brand) => brand.label)
              .join(", ")}
          </p>
        )}
      </div>

      <RatingFilter
        onChange={(nextRating) => updateFilter({ min_rating: nextRating })}
        options={ratings}
        value={query.min_rating}
      />

      <label
        className='flex cursor-pointer items-center gap-2 text-gray-700 text-sm'
        htmlFor='category-discount'
      >
        <input
          checked={query.discount !== null}
          className='h-4 w-4 accent-primary'
          id='category-discount'
          onChange={(event) =>
            updateFilter({ discount: event.target.checked ? "1" : null })
          }
          type='checkbox'
        />
        <span>Somente descontos</span>
      </label>
    </aside>
  )
}
